// src/views/ClientsView.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Plus, Search, Edit2, Trash2, X, MessageCircle, UserPlus, User,
  Phone, Mail, Star, Key, CheckCircle, XCircle, ChevronRight,
  ChevronDown, Download, Globe, Home
} from "lucide-react";
import OwnerNotesModal from "../components/owners/OwnerNotesModal";
import OwnerExportModal from "../components/owners/OwnerExportModal";
import OwnerActionsMenu from "../components/owners/OwnerActionsMenu";

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Badge visuel LOCAL / OFFSITE
const OwnerTypeBadge = ({ type }) => {
  if (type === 'offsite') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/30">
        <Globe size={10} /> Offsite
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
      <Home size={10} /> Local
    </span>
  );
};

const ClientsView = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // ← NOUVEAU: filtre local/offsite/all
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [expandedClientId, setExpandedClientId] = useState(null);
  
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedClientForNotes, setSelectedClientForNotes] = useState(null);
  const [selectedClientsForExport, setSelectedClientsForExport] = useState([]);
  const [exportSelectionMode, setExportSelectionMode] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState(new Set());

  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    contact_type: "assistant", first_name: "", last_name: "",
    email: "", phone: "", phone_mobile: "", is_primary: false, notes: ""
  });

  const [formData, setFormData] = useState({
    email: "", first_name: "", last_name: "", company_name: "",
    phone: "", phone_mobile: "", phone_office: "",
    address: "", city: "", state: "TX", zip_code: "",
    charge_landing_fees: true, charge_takeoff_fees: true,
    fuel_discount_percent: 0,
    pilot_license_number: "", pilot_license_expiry: "",
    vehicle_plate: "", vehicle_make: "", vehicle_model: "", vehicle_color: "",
    access_code: "", access_card_id: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    notes: "", telegram_username: "", telegram_notifications: false,
    is_active: true,
    owner_type: "local",  // ← NOUVEAU
  });

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE}/owners/`, { withCredentials: true });
      setClients(response.data);
    } catch (err) {
      console.error("API Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async (ownerId) => {
    if (!ownerId) return;
    setLoadingContacts(true);
    try {
      const response = await axios.get(`${API_BASE}/owners/${ownerId}/contacts`, { withCredentials: true });
      setContacts(response.data);
    } catch (err) {
      setContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleResetPassword = async () => {
    if (!editingClient?.id) return;
    setResettingPassword(true);
    setResetPasswordResult(null);
    try {
      const response = await axios.post(
        `${API_BASE}/owners/${editingClient.id}/reset-password`, {},
        { withCredentials: true }
      );
      setResetPasswordResult(response.data.new_password);
    } catch (err) {
      alert("Failed to reset password: " + (err.response?.data?.detail || err.message));
    } finally {
      setResettingPassword(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  useEffect(() => {
    if (showModal && editingClient) {
      fetchContacts(editingClient.id);
    } else if (showModal && !editingClient) {
      setContacts([]);
    }
    setResetPasswordResult(null);
  }, [showModal, editingClient]);

  const handleSubmit = async () => {
    try {
      const dataToSend = {};
      for (const [key, value] of Object.entries(formData)) {
        if (value !== "" && value !== null && value !== undefined) {
          dataToSend[key] = value;
        }
      }
      if (editingClient) {
        await axios.put(`${API_BASE}/owners/${editingClient.id}`, dataToSend, { withCredentials: true });
      } else {
        await axios.post(`${API_BASE}/owners/`, dataToSend, { withCredentials: true });
      }
      fetchClients();
      setShowModal(false);
      resetForm();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveContact = async () => {
    if (!editingClient?.id) {
      alert("Please save the client first before adding contacts");
      return;
    }
    try {
      const dataToSend = {};
      for (const [key, value] of Object.entries(contactForm)) {
        if (value !== "" && value !== null && value !== undefined) {
          dataToSend[key] = value;
        }
      }
      if (editingContact) {
        await axios.put(`${API_BASE}/owners/contacts/${editingContact.id}`, dataToSend, { withCredentials: true });
      } else {
        await axios.post(`${API_BASE}/owners/${editingClient.id}/contacts`, dataToSend, { withCredentials: true });
      }
      resetContactForm();
      fetchContacts(editingClient.id);
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      contact_type: contact.contact_type || "assistant",
      first_name: contact.first_name || "", last_name: contact.last_name || "",
      email: contact.email || "", phone: contact.phone || "",
      phone_mobile: contact.phone_mobile || "",
      is_primary: contact.is_primary || false, notes: contact.notes || ""
    });
    setShowContactForm(true);
  };

  const handleDeleteContact = async (contactId) => {
    if (window.confirm("Delete this contact?")) {
      try {
        await axios.delete(`${API_BASE}/owners/contacts/${contactId}`, { withCredentials: true });
        fetchContacts(editingClient.id);
      } catch (err) {
        alert("Delete failed: " + err.message);
      }
    }
  };

  const resetContactForm = () => {
    setContactForm({
      contact_type: "assistant", first_name: "", last_name: "",
      email: "", phone: "", phone_mobile: "", is_primary: false, notes: ""
    });
    setEditingContact(null);
    setShowContactForm(false);
  };

  const resetForm = () => {
    setFormData({
      email: "", first_name: "", last_name: "", company_name: "",
      phone: "", phone_mobile: "", phone_office: "",
      address: "", city: "", state: "TX", zip_code: "",
      charge_landing_fees: true, charge_takeoff_fees: true, fuel_discount_percent: 0,
      pilot_license_number: "", pilot_license_expiry: "",
      vehicle_plate: "", vehicle_make: "", vehicle_model: "", vehicle_color: "",
      access_code: "", access_card_id: "",
      emergency_contact_name: "", emergency_contact_phone: "",
      notes: "", telegram_username: "", telegram_notifications: false,
      is_active: true, owner_type: "local",
    });
    setEditingClient(null);
    setActiveTab("general");
    setContacts([]);
    resetContactForm();
    setResetPasswordResult(null);
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      email: client.email || "",
      first_name: client.first_name || "", last_name: client.last_name || "",
      company_name: client.company_name || "",
      phone: client.phone || "", phone_mobile: client.phone_mobile || "",
      phone_office: client.phone_office || "",
      address: client.address || "", city: client.city || "",
      state: client.state || "TX", zip_code: client.zip_code || "",
      charge_landing_fees: client.charge_landing_fees !== undefined ? client.charge_landing_fees : true,
      charge_takeoff_fees: client.charge_takeoff_fees !== undefined ? client.charge_takeoff_fees : true,
      fuel_discount_percent: client.fuel_discount_percent || 0,
      pilot_license_number: client.pilot_license_number || "",
      pilot_license_expiry: client.pilot_license_expiry || "",
      vehicle_plate: client.vehicle_plate || "", vehicle_make: client.vehicle_make || "",
      vehicle_model: client.vehicle_model || "", vehicle_color: client.vehicle_color || "",
      access_code: client.access_code || "", access_card_id: client.access_card_id || "",
      emergency_contact_name: client.emergency_contact_name || "",
      emergency_contact_phone: client.emergency_contact_phone || "",
      notes: client.notes || "", telegram_username: client.telegram_username || "",
      telegram_notifications: client.telegram_notifications || false,
      is_active: client.is_active !== undefined ? client.is_active : true,
      owner_type: client.owner_type || "local",  // ← NOUVEAU
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete or deactivate this client?")) {
      try {
        await axios.delete(`${API_BASE}/owners/${id}?force=false`, { withCredentials: true });
        fetchClients();
      } catch (err) {
        alert("Delete failed: " + err.message);
      }
    }
  };

  const handleOpenNotes = (client) => {
    setSelectedClientForNotes(client);
    setShowNotesModal(true);
  };

  const handleOpenExport = () => {
    if (selectedClientIds.size === 0) {
      alert("Please select at least one client to export");
      return;
    }
    setSelectedClientsForExport(clients.filter(c => selectedClientIds.has(c.id)));
    setShowExportModal(true);
  };

  const toggleClientSelection = (clientId) => {
    const newSet = new Set(selectedClientIds);
    newSet.has(clientId) ? newSet.delete(clientId) : newSet.add(clientId);
    setSelectedClientIds(newSet);
  };

  const toggleAllSelection = () => {
    if (selectedClientIds.size === filteredClients.length) {
      setSelectedClientIds(new Set());
    } else {
      setSelectedClientIds(new Set(filteredClients.map(c => c.id)));
    }
  };

  // Filtre recherche + type
  const filteredClients = clients.filter((c) => {
    const matchSearch =
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.telegram_username?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchType =
      filterType === "all" ||
      (filterType === "local" && (!c.owner_type || c.owner_type === "local")) ||
      (filterType === "offsite" && c.owner_type === "offsite");

    return matchSearch && matchType;
  });

  const localCount = clients.filter(c => !c.owner_type || c.owner_type === 'local').length;
  const offsiteCount = clients.filter(c => c.owner_type === 'offsite').length;

  if (loading) return <div className="text-center py-10 text-gray-400">Loading from database...</div>;
  if (error) return (
    <div className="text-center py-10 px-4">
      <div className="bg-red-500/20 text-red-400 p-4 rounded-lg inline-block max-w-full">
        <p className="mb-4">Network Error</p>
        <p className="text-sm mb-4 break-all">Make sure backend is running on {API_BASE}</p>
        <button onClick={fetchClients} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Retry</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white">Clients</h2>
          <p className="text-green-400 text-xs md:text-sm">
            {clients.length} total — <span className="text-blue-400">{localCount} local</span>
            {offsiteCount > 0 && <span className="text-orange-400"> · {offsiteCount} offsite</span>}
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {exportSelectionMode ? (
            <>
              <button onClick={() => { setExportSelectionMode(false); setSelectedClientIds(new Set()); }}
                className="px-3 py-2 bg-gray-700 rounded-lg text-sm">Cancel</button>
              <button onClick={handleOpenExport} disabled={selectedClientIds.size === 0}
                className="px-3 py-2 bg-green-600 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
                <Download size={16} /> Export ({selectedClientIds.size})
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setExportSelectionMode(true)}
                className="px-3 py-2 bg-gray-700 rounded-lg text-sm flex items-center gap-2">
                <Download size={16} /> Export
              </button>
              <button onClick={() => { resetForm(); setShowModal(true); }}
                className="bg-blue-600 px-3 py-2 sm:px-4 rounded-lg flex items-center gap-2 text-sm w-full sm:w-auto justify-center">
                <Plus size={18} /> Add Client
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
          <input type="text" placeholder="Search by name, email, or Telegram..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white text-sm" />
        </div>
        {/* Filtre LOCAL / OFFSITE */}
        <div className="flex rounded-lg overflow-hidden border border-gray-700 text-sm">
          {[["all", "All"], ["local", "🏠 Local"], ["offsite", "✈️ Offsite"]].map(([val, label]) => (
            <button key={val} onClick={() => setFilterType(val)}
              className={`px-3 py-2 transition ${filterType === val ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Clients List */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr>
                {exportSelectionMode && (
                  <th className="p-4 w-10">
                    <input type="checkbox"
                      checked={selectedClientIds.size === filteredClients.length && filteredClients.length > 0}
                      onChange={toggleAllSelection} className="w-4 h-4 accent-blue-500" />
                  </th>
                )}
                <th className="p-4 text-left text-gray-400 text-sm">Client</th>
                <th className="p-4 text-left text-gray-400 text-sm">Type</th>
                <th className="p-4 text-left text-gray-400 text-sm">Contact</th>
                <th className="p-4 text-left text-gray-400 text-sm">Status</th>
                <th className="p-4 text-left text-gray-400 text-sm">Telegram</th>
                <th className="p-4 text-left text-gray-400 text-sm">License</th>
                <th className="p-4 text-left text-gray-400 text-sm">Vehicle</th>
                <th className="p-4 text-left text-gray-400 text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((c) => (
                <tr key={c.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                  {exportSelectionMode && (
                    <td className="p-4">
                      <input type="checkbox" checked={selectedClientIds.has(c.id)}
                        onChange={() => toggleClientSelection(c.id)} className="w-4 h-4 accent-blue-500" />
                    </td>
                  )}
                  <td className="p-4">
                    <p className="text-white font-medium">{c.company_name || `${c.first_name} ${c.last_name}`}</p>
                    <p className="text-gray-500 text-xs">{c.email}</p>
                  </td>
                  <td className="p-4">
                    <OwnerTypeBadge type={c.owner_type} />
                  </td>
                  <td className="p-4">
                    <p className="text-gray-300">{c.phone || "N/A"}</p>
                    <p className="text-gray-500 text-xs">{c.city}</p>
                  </td>
                  <td className="p-4">
                    {c.is_active ? (
                      <span className="flex items-center gap-1 text-green-400 text-xs"><CheckCircle size={12} /> Active</span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-400 text-xs"><XCircle size={12} /> Inactive</span>
                    )}
                  </td>
                  <td className="p-4">
                    {c.telegram_username ? (
                      <div className="flex items-center gap-1">
                        <MessageCircle size={14} className="text-blue-400" />
                        <span className="text-blue-400 text-sm truncate max-w-[100px]">{c.telegram_username}</span>
                        {c.telegram_notifications && <span className="ml-1 text-xs text-green-400">✓</span>}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-xs">Not set</span>
                    )}
                  </td>
                  <td className="p-4">
                    {c.pilot_license_number && <span className="text-xs text-blue-400">License: {c.pilot_license_number}</span>}
                  </td>
                  <td className="p-4">
                    {c.vehicle_plate && <span className="text-xs text-gray-400">{c.vehicle_plate}</span>}
                  </td>
                  <td className="p-4">
                    <OwnerActionsMenu owner={c} onEdit={() => handleEdit(c)} onDelete={() => handleDelete(c.id)}
                      onOpenNotes={() => handleOpenNotes(c)}
                      onResetPassword={() => { setEditingClient(c); setShowModal(true); setActiveTab("account"); }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-gray-700">
          {filteredClients.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No clients found</div>
          ) : (
            filteredClients.map((c) => (
              <div key={c.id} className="p-3">
                <div className="flex items-start justify-between">
                  {exportSelectionMode && (
                    <input type="checkbox" checked={selectedClientIds.has(c.id)}
                      onChange={() => toggleClientSelection(c.id)}
                      className="w-4 h-4 accent-blue-500 mt-1 mr-2" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-medium text-sm truncate">
                        {c.company_name || `${c.first_name} ${c.last_name}`}
                      </p>
                      <OwnerTypeBadge type={c.owner_type} />
                    </div>
                    <p className="text-gray-500 text-xs truncate">{c.email}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {c.is_active ? (
                        <span className="flex items-center gap-1 text-green-400 text-xs"><CheckCircle size={10} /> Active</span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400 text-xs"><XCircle size={10} /> Inactive</span>
                      )}
                      {c.telegram_username && (
                        <span className="flex items-center gap-1 text-blue-400 text-xs">
                          <MessageCircle size={10} /> @{c.telegram_username}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-2">
                    <OwnerActionsMenu owner={c} onEdit={() => handleEdit(c)} onDelete={() => handleDelete(c.id)}
                      onOpenNotes={() => handleOpenNotes(c)}
                      onResetPassword={() => { setEditingClient(c); setShowModal(true); setActiveTab("account"); }} />
                    <button onClick={() => setExpandedClientId(expandedClientId === c.id ? null : c.id)}
                      className="text-gray-400 p-1">
                      {expandedClientId === c.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  </div>
                </div>
                {expandedClientId === c.id && (
                  <div className="mt-3 pt-3 border-t border-gray-700 space-y-2">
                    <div className="flex items-center gap-2 text-xs">
                      <Phone size={12} className="text-gray-500" />
                      <span className="text-gray-300">{c.phone || "No phone"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <Mail size={12} className="text-gray-500" />
                      <span className="text-gray-300">{c.email}</span>
                    </div>
                    {c.city && <div className="text-xs text-gray-400">📍 {c.city}, {c.state}</div>}
                    {c.pilot_license_number && <div className="text-xs text-blue-400">✈️ License: {c.pilot_license_number}</div>}
                    {c.vehicle_plate && <div className="text-xs text-gray-400">🚗 {c.vehicle_plate}</div>}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Modal (Edit/Create Client) */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto p-2 sm:p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-[95%] sm:max-w-4xl my-4 sm:my-8">
            <div className="p-4 sm:p-6 border-b border-gray-700 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  {editingClient ? "Edit Client" : "New Client"}
                </h3>
                {editingClient && <OwnerTypeBadge type={formData.owner_type} />}
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white p-1">
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-700 px-3 sm:px-6 overflow-x-auto whitespace-nowrap">
              {["general", "contact", "contacts", "pilot", "vehicle", "access", "emergency", "telegram", "account"].map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`px-3 sm:px-4 py-2 text-xs sm:text-sm capitalize transition flex items-center gap-1 ${activeTab === tab ? "text-blue-400 border-b-2 border-blue-400" : "text-gray-400 hover:text-white"}`}>
                  {tab === "telegram" && <MessageCircle size={12} />}
                  {tab === "contacts" && <UserPlus size={12} />}
                  {tab === "account" && <Key size={12} />}
                  {tab}
                </button>
              ))}
            </div>

            <div className="p-4 sm:p-6 max-h-[55vh] sm:max-h-[60vh] overflow-y-auto">

              {/* General Tab */}
              {activeTab === "general" && (
                <div className="space-y-4">
                  {/* Toggle LOCAL / OFFSITE */}
                  <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                    <div>
                      <p className="text-white text-sm font-medium">Client Type</p>
                      <p className="text-gray-400 text-xs">
                        {formData.owner_type === 'local'
                          ? 'Local: has a portal account, billed internally'
                          : 'Offsite: no portal, billed via Stripe payment link'}
                      </p>
                    </div>
                    <div className="flex rounded-lg overflow-hidden border border-gray-600 text-xs">
                      <button
                        onClick={() => setFormData({ ...formData, owner_type: 'local' })}
                        className={`px-3 py-1.5 flex items-center gap-1 transition ${formData.owner_type === 'local' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}>
                        <Home size={12} /> Local
                      </button>
                      <button
                        onClick={() => setFormData({ ...formData, owner_type: 'offsite' })}
                        className={`px-3 py-1.5 flex items-center gap-1 transition ${formData.owner_type === 'offsite' ? 'bg-orange-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}>
                        <Globe size={12} /> Offsite
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <input type="text" placeholder="First Name" value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                    <input type="text" placeholder="Last Name" value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                    <input type="email" placeholder="Email *" value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm sm:col-span-2" />
                    <input type="text" placeholder="Company Name" value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm sm:col-span-2" />
                    <input type="text" placeholder="Address" value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm sm:col-span-2" />
                    <input type="text" placeholder="City" value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                    <select value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                      <option value="TX">Texas</option><option value="CA">California</option>
                      <option value="FL">Florida</option><option value="NY">New York</option>
                    </select>
                    <input type="text" placeholder="Zip Code" value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                </div>
              )}

              {/* Contact Tab */}
              {activeTab === "contact" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <input type="tel" placeholder="Phone (Main)" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  <input type="tel" placeholder="Phone (Mobile)" value={formData.phone_mobile}
                    onChange={(e) => setFormData({ ...formData, phone_mobile: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  <input type="tel" placeholder="Phone (Office)" value={formData.phone_office}
                    onChange={(e) => setFormData({ ...formData, phone_office: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm sm:col-span-2" />
                  <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 sm:gap-4 mt-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={formData.charge_landing_fees}
                        onChange={(e) => setFormData({ ...formData, charge_landing_fees: e.target.checked })} />
                      Landing Fees
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={formData.charge_takeoff_fees}
                        onChange={(e) => setFormData({ ...formData, charge_takeoff_fees: e.target.checked })} />
                      Takeoff Fees
                    </label>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400">Fuel Discount:</span>
                      <input type="number" value={formData.fuel_discount_percent}
                        onChange={(e) => setFormData({ ...formData, fuel_discount_percent: parseInt(e.target.value) })}
                        className="w-20 bg-gray-700 rounded-lg p-1 text-white text-center" />%
                    </div>
                  </div>
                </div>
              )}

              {/* Contacts Tab */}
              {activeTab === "contacts" && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <h4 className="text-white font-medium text-sm">Additional Contacts</h4>
                    <button onClick={() => setShowContactForm(true)}
                      className="text-sm bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded-lg flex items-center gap-1 w-full sm:w-auto justify-center">
                      <UserPlus size={14} /> Add Contact
                    </button>
                  </div>
                  {loadingContacts ? (
                    <div className="text-center py-8 text-gray-400">Loading contacts...</div>
                  ) : contacts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 border border-dashed border-gray-600 rounded-lg">
                      <User size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No additional contacts</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {contacts.map((contact) => (
                        <div key={contact.id} className="bg-gray-700/30 rounded-lg p-3">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              {contact.is_primary && <Star size={14} className="text-yellow-400" />}
                              <span className="text-white font-medium text-sm">{contact.first_name} {contact.last_name}</span>
                              <span className="text-xs text-gray-400 capitalize">({contact.contact_type})</span>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => handleEditContact(contact)} className="text-blue-400"><Edit2 size={14} /></button>
                              <button onClick={() => handleDeleteContact(contact.id)} className="text-red-400"><Trash2 size={14} /></button>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-400">
                            {contact.email && <span className="flex items-center gap-1"><Mail size={10} /> {contact.email}</span>}
                            {contact.phone && <span className="flex items-center gap-1"><Phone size={10} /> {contact.phone}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Pilot Tab */}
              {activeTab === "pilot" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <input type="text" placeholder="Pilot License Number" value={formData.pilot_license_number}
                    onChange={(e) => setFormData({ ...formData, pilot_license_number: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  <input type="date" value={formData.pilot_license_expiry}
                    onChange={(e) => setFormData({ ...formData, pilot_license_expiry: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
              )}

              {/* Vehicle Tab */}
              {activeTab === "vehicle" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <input type="text" placeholder="Vehicle Plate" value={formData.vehicle_plate}
                    onChange={(e) => setFormData({ ...formData, vehicle_plate: e.target.value.toUpperCase() })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  <input type="text" placeholder="Vehicle Make" value={formData.vehicle_make}
                    onChange={(e) => setFormData({ ...formData, vehicle_make: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  <input type="text" placeholder="Vehicle Model" value={formData.vehicle_model}
                    onChange={(e) => setFormData({ ...formData, vehicle_model: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  <input type="text" placeholder="Vehicle Color" value={formData.vehicle_color}
                    onChange={(e) => setFormData({ ...formData, vehicle_color: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
              )}

              {/* Access Tab */}
              {activeTab === "access" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <input type="text" placeholder="Access Code (PIN)" value={formData.access_code}
                    onChange={(e) => setFormData({ ...formData, access_code: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  <input type="text" placeholder="Access Card ID / RFID" value={formData.access_card_id}
                    onChange={(e) => setFormData({ ...formData, access_card_id: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
              )}

              {/* Emergency Tab */}
              {activeTab === "emergency" && (
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                  <input type="text" placeholder="Emergency Contact Name" value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  <input type="tel" placeholder="Emergency Contact Phone" value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                    className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  <textarea placeholder="Additional Notes" value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3" className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                </div>
              )}

              {/* Telegram Tab */}
              {activeTab === "telegram" && (
                <div className="space-y-4">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageCircle size={16} className="text-blue-400" />
                      <h4 className="text-white font-medium text-sm">Telegram Notifications</h4>
                    </div>
                    <p className="text-gray-400 text-xs sm:text-sm">Clients with Telegram can receive instant alerts.</p>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Telegram Username</label>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">@</span>
                      <input type="text" placeholder="username" value={formData.telegram_username}
                        onChange={(e) => setFormData({ ...formData, telegram_username: e.target.value.replace('@', '') })}
                        className="flex-1 bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={formData.telegram_notifications}
                      onChange={(e) => setFormData({ ...formData, telegram_notifications: e.target.checked })}
                      className="w-4 h-4 accent-blue-500" />
                    <span className="text-white">Enable Telegram notifications</span>
                  </label>
                </div>
              )}

              {/* Account Tab */}
              {activeTab === "account" && (
                <div className="space-y-4">
                  {formData.owner_type === 'offsite' && (
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                      <p className="text-orange-400 text-xs">
                        ✈️ Offsite client — no portal access. Billing is done via Stripe payment link.
                      </p>
                    </div>
                  )}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Key size={16} className="text-yellow-400" />
                      <h4 className="text-white font-medium text-sm">Client Portal Access</h4>
                    </div>
                    <p className="text-gray-400 text-xs sm:text-sm">Enable this client to access their dashboard.</p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-gray-700/30 rounded-lg gap-3">
                    <div className="flex items-center gap-3">
                      {formData.is_active ? <CheckCircle size={20} className="text-green-400" /> : <XCircle size={20} className="text-red-400" />}
                      <div>
                        <p className="text-white font-medium text-sm">Account Status</p>
                        <p className="text-xs text-gray-400">
                          {formData.is_active ? "Active - Client can log in" : "Inactive - Client cannot access portal"}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-green-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </label>
                  </div>

                  <div className="p-3 bg-gray-700/30 rounded-lg">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3">
                      <div>
                        <p className="text-white font-medium text-sm">Password Management</p>
                        <p className="text-xs text-gray-400">Reset password and send to client</p>
                      </div>
                      <button onClick={handleResetPassword} disabled={resettingPassword}
                        className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 rounded-lg text-white text-sm flex items-center gap-2 disabled:opacity-50 w-full sm:w-auto justify-center">
                        {resettingPassword ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Key size={14} />}
                        Reset Password
                      </button>
                    </div>
                    {resetPasswordResult && (
                      <div className="mt-3 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                        <p className="text-green-400 text-sm font-medium mb-1">New Password Generated:</p>
                        <p className="text-white text-sm sm:text-lg font-mono break-all">{resetPasswordResult}</p>
                        <p className="text-yellow-400 text-xs mt-2">⚠️ Copy this password now - it won't be shown again!</p>
                      </div>
                    )}
                  </div>

                  {editingClient && (
                    <div className="p-3 bg-gray-700/30 rounded-lg">
                      <p className="text-white font-medium text-sm mb-2">Login Information</p>
                      <p className="text-xs sm:text-sm text-gray-400 break-all">
                        <span className="text-gray-500">Email:</span> {editingClient.email}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Last login: {editingClient.last_login ? new Date(editingClient.last_login).toLocaleString() : 'Never'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-6 border-t border-gray-700 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-700 rounded-lg text-sm w-full sm:w-auto">Cancel</button>
              <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 rounded-lg text-sm w-full sm:w-auto">Save Client</button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto p-2 sm:p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-[95%] sm:max-w-md my-4">
            <div className="p-4 sm:p-6 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-base sm:text-lg font-bold text-white">{editingContact ? "Edit Contact" : "Add Contact"}</h3>
              <button onClick={resetContactForm} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <select value={contactForm.contact_type}
                onChange={(e) => setContactForm({ ...contactForm, contact_type: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                <option value="assistant">Assistant</option>
                <option value="manager">Fleet Manager</option>
                <option value="billing">Billing Contact</option>
                <option value="dispatcher">Dispatcher</option>
                <option value="pilot">Pilot</option>
                <option value="other">Other</option>
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input type="text" placeholder="First Name" value={contactForm.first_name}
                  onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                  className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                <input type="text" placeholder="Last Name" value={contactForm.last_name}
                  onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                  className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <input type="email" placeholder="Email" value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              <input type="tel" placeholder="Phone" value={contactForm.phone}
                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              <input type="tel" placeholder="Mobile Phone" value={contactForm.phone_mobile}
                onChange={(e) => setContactForm({ ...contactForm, phone_mobile: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={contactForm.is_primary}
                  onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-600" />
                <span className="text-gray-300">Primary Contact</span>
              </label>
              <textarea placeholder="Notes" value={contactForm.notes}
                onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                rows="2" className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-700 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <button onClick={resetContactForm} className="px-4 py-2 bg-gray-700 rounded-lg text-sm w-full sm:w-auto">Cancel</button>
              <button onClick={handleSaveContact} className="px-4 py-2 bg-blue-600 rounded-lg text-sm w-full sm:w-auto">Save Contact</button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      <OwnerNotesModal owner={selectedClientForNotes} isOpen={showNotesModal}
        onClose={() => { setShowNotesModal(false); setSelectedClientForNotes(null); }}
        onNoteAdded={fetchClients} />

      {/* Export Modal */}
      <OwnerExportModal selectedOwners={selectedClientsForExport} isOpen={showExportModal}
        onClose={() => { setShowExportModal(false); setSelectedClientsForExport([]); setExportSelectionMode(false); setSelectedClientIds(new Set()); }} />
    </div>
  );
};

export default ClientsView;